// src/contexts/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { createAppTheme } from '@/core/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme mode with session awareness
  const [mode, setMode] = useState(() => {
    // Check if this is a session continuation (refresh/navigation) vs fresh login
    const isSessionContinuation = sessionStorage.getItem('theme_session_active');
    
    if (isSessionContinuation) {
      // Session continuation - use localStorage (preserves manual changes during session)
      const savedMode = localStorage.getItem('themeMode');
      return savedMode || 'light';
    } else {
      // Fresh login - will be set by preference sync, start with light default
      // Mark session as active for future refreshes
      sessionStorage.setItem('theme_session_active', 'true');
      return 'light';
    }
  });

  // Create theme based on current mode
  const theme = createAppTheme(mode);

  // Save theme mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  }, []);

  const setThemeMode = useCallback((newMode) => {
    if (newMode === 'light' || newMode === 'dark') {
      setMode(newMode);
    }
  }, []);

  const value = useMemo(() => ({
    mode,
    theme,
    toggleTheme,
    setThemeMode,
    isDark: mode === 'dark',
    isLight: mode === 'light',
  }), [mode, theme, toggleTheme, setThemeMode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeProvider; 
